import { CylinderGeometry, Group, Mesh, MeshPhysicalMaterial } from 'three';
import { drawnTexture } from '../arena/canvasTexture';
import { blob, lathe } from './shapes';

/** From the wrist, the glove's origin, to the tip of its knuckles. */
export const gloveLength = 0.315;

export interface GloveColours {
  readonly leather: number;
  readonly trim: number;
  readonly label: string;
}

/**
 * A boxing glove, knuckles along -Z from the wrist at its origin: a padded fist with the thumb along its side, a cuff as
 * wide as the forearm with a dark band at its end, and a white label on the back, as on the family's reference picture.
 * side is +1 for a left glove, -1 for a right one: the thumb is on the inside.
 */
export function buildGlove(colours: GloveColours, side: 1 | -1): Group {
  const glove = new Group();
  // Worn leather: a satin sheen, not a lacquered shine.
  const leather = new MeshPhysicalMaterial({ color: colours.leather, roughness: 0.5, clearcoat: 0.25, clearcoatRoughness: 0.45 });

  const fist = new Mesh(blob(0.115, 0.108, 0.145, 32), leather);
  fist.position.set(0, 0.012, -0.17);
  glove.add(fist);
  // The rolled-over fingers on top of the fist, the way a real glove's knuckle pad bulges.
  const knuckles = new Mesh(blob(0.1, 0.07, 0.11, 28), leather);
  knuckles.position.set(0, 0.06, -0.2);
  glove.add(knuckles);
  const thumb = new Mesh(blob(0.042, 0.05, 0.095, 20), leather);
  thumb.position.set(side * 0.085, -0.015, -0.13);
  thumb.rotation.set(0.15, side * 0.35, side * 0.4);
  glove.add(thumb);

  // The cuff: a short tube hugging the wrist, flaring a little towards the hand.
  const cuff = new Mesh(lathe([[0.084, 0], [0.086, 0.05], [0.092, 0.11], [0.098, 0.14]], 0.95, 36), leather);
  cuff.rotation.x = -Math.PI / 2;
  cuff.position.z = 0.02;
  glove.add(cuff);
  // The dark stripe round the cuff's open end, sitting just proud of the leather.
  const band = new Mesh(lathe([[0.0852, 0], [0.0872, 0.004], [0.0878, 0.024], [0.0868, 0.028]], 0.95, 36), new MeshPhysicalMaterial({ color: colours.trim, roughness: 0.5, clearcoat: 0.4 }));
  band.rotation.x = -Math.PI / 2;
  band.position.z = 0.02;
  glove.add(band);

  const labelTexture = drawnTexture(256, 128, (g, w, h) => {
    g.fillStyle = '#f4f1ea';
    g.fillRect(0, 0, w, h);
    g.strokeStyle = '#1a1a1a';
    g.lineWidth = 8;
    g.strokeRect(6, 6, w - 12, h - 12);
    g.fillStyle = '#1a1a1a';
    g.font = `400 ${h * 0.62}px "Russo One", sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(colours.label, w / 2, h / 2 + 4);
  });
  const label = new Mesh(new CylinderGeometry(0.097, 0.097, 0.055, 24, 1, true, -0.55, 1.1), new MeshPhysicalMaterial({ map: labelTexture, roughness: 0.6 }));
  label.rotation.x = -Math.PI / 2;
  label.position.z = -0.05;
  glove.add(label);

  glove.traverse((part) => {
    if (part instanceof Mesh) part.castShadow = true;
  });
  return glove;
}
